// components/dashboard/ProssimeAttivita.js
// PROSSIME ATTIVITÀ (§16-§27) — widget unificato PM/Tecnico: appuntamenti reali + next step attivi.
// Sostituisce "Prossimi appuntamenti" su PM e Tecnico. Direzione resta invariata.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CalendarDays, Compass, AlertTriangle, Clock, MapPin, ChevronDown, ChevronUp, Inbox } from 'lucide-react';

const PAGE = 3;

const KindIcon = ({ a }) => {
  if (a.kind === 'appointment') return <CalendarDays className="w-4 h-4 text-[#1D6FD1]" />;
  if (a.state === 'overdue') return <AlertTriangle className="w-4 h-4 text-[#C0392B]" />;
  return <Compass className="w-4 h-4 text-[#C77700]" />;
};

const Row = ({ a, onClick }) => {
  const overdue = a.state === 'overdue';
  return (
    <button onClick={onClick} data-testid={`attivita-${a.id}`}
      className="w-full text-left flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors group">
      <div className="flex items-start gap-2.5 min-w-0">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          a.kind === 'appointment' ? 'bg-[#E5F4FF]' : overdue ? 'bg-[#FDECEC]' : 'bg-[#FFF4E0]'
        }`}>
          <KindIcon a={a} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{a.client_name}</p>
          <p className="text-xs text-gray-500 truncate flex items-center gap-1">
            <span className="uppercase text-[9px] font-bold tracking-wide text-gray-400">
              {a.kind === 'appointment' ? 'Appuntamento' : 'Next step'}
            </span>
            · {a.title}
            {a.location ? <><span className="text-gray-300">·</span><MapPin className="w-3 h-3" />{a.location}</> : null}
          </p>
          {a.detail ? <p className={`text-[11px] mt-0.5 ${overdue ? 'text-[#C0392B] font-semibold' : 'text-gray-500'}`}>{a.detail}</p> : null}
          {a.responsibility ? <p className="text-[10px] text-gray-400 mt-0.5">{a.responsibility}</p> : null}
        </div>
      </div>
      <div className="flex flex-col items-end shrink-0 pl-2">
        <span className={`text-[11px] font-bold uppercase ${
          overdue ? 'text-[#C0392B]' : a.is_today ? 'text-[#FF7819]' : 'text-gray-500'
        }`}>{a.date_label}</span>
        {a.time && a.time !== '00:00' ? <span className="text-xs text-gray-500 flex items-center gap-0.5"><Clock className="w-3 h-3" />{a.time}</span> : null}
      </div>
    </button>
  );
};

export const ProssimeAttivita = ({ activities = [], summary = null, initialLimit = PAGE, onExpandChange }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(initialLimit || activities.length);
  const shown = initialLimit ? activities.slice(0, visible) : activities;
  useEffect(() => { onExpandChange && onExpandChange(!!initialLimit && visible > initialLimit); }, [visible, initialLimit, onExpandChange]);

  const scadute = summary?.scadute || 0;
  const oggi = summary?.oggi || 0;

  return (
    <Card className="bg-white rounded-xl border border-[#E4E4E4] shadow-sm h-full flex flex-col" data-testid="prossime-attivita">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0 flex-wrap">
            <Compass className="w-5 h-5 text-[#FF7819] shrink-0" />
            <span className="truncate">Prossime attività</span>
            {(scadute > 0 || oggi > 0) && (
              <span className="text-xs font-normal flex items-center gap-1.5 shrink-0" data-testid="prossime-attivita-summary">
                <span className="text-gray-300">·</span>
                {scadute > 0 && <span className="text-[#C0392B] font-semibold">⚠ {scadute} scadute</span>}
                {scadute > 0 && oggi > 0 && <span className="text-gray-300">·</span>}
                {oggi > 0 && <span className="text-[#FF7819] font-medium">{oggi} oggi</span>}
              </span>
            )}
          </span>
          <span className="text-sm font-normal text-gray-400 shrink-0">{activities.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="prossime-attivita-empty">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-2"><Inbox className="w-6 h-6 text-gray-400" /></div>
            <p className="text-sm font-medium text-gray-600">Nessuna attività in programma</p>
          </div>
        ) : (
          <>
            <div className="space-y-0.5" data-testid="prossime-attivita-list">
              {shown.map((a) => <Row key={a.id} a={a} onClick={() => a.link && navigate(a.link)} />)}
            </div>
            {initialLimit && activities.length > initialLimit && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500">{Math.min(visible, activities.length)} di {activities.length}</span>
                <div className="flex items-center gap-3">
                  {visible < activities.length && (
                    <button onClick={() => setVisible((v) => v + PAGE)} data-testid="prossime-attivita-more"
                      className="text-xs font-medium text-[#FF7819] hover:underline flex items-center gap-1">
                      Mostra altri {Math.min(PAGE, activities.length - visible)} <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {visible > initialLimit && (
                    <button onClick={() => setVisible(initialLimit)} data-testid="prossime-attivita-less"
                      className="text-xs font-medium text-gray-500 hover:underline flex items-center gap-1">
                      Riduci <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProssimeAttivita;
